import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Header from 'components/ui/Header';
import Footer from '../landing-page/components/Footer';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import { Checkbox } from 'components/ui/Checkbox';
import Icon from 'components/AppIcon';

const PROFESSIONS = [
'Antifurti e videosorveglianza',
'Cartongessista',
'Demolizioni',
'Ditta isolamento',
'Ditta traslochi',
'Edilizia',
'Elettricista',
'Falegname',
'Giardiniere',
'Imbianchino',
'Impresa di Pulizia',
'Infissi e serramenti',
'Pavimentazione',
'Rifacimento facciata',
'Riparazione tetti',
'Specialista bagni',
'Specialista cucine',
'Tappezziere',
'Termoidraulica',
'Tuttofare'];

const detailedRows = [
{
  label: 'Tempo di registrazione',
  sub: 'Prima di poter rispondere ai clienti',
  concorrenza: { text: '20-30 min', color: '#b45309' },
  volaptic: { text: '2 min', color: '#166534', fontWeight: 600 }
},
{
  label: 'Paghi per ogni risposta',
  sub: 'Anche se il cliente sparisce',
  concorrenza: { text: 'Sì', color: '#dc2626' },
  volaptic: { text: 'No', color: '#16a34a' }
},
{
  label: 'Devi comprare crediti anticipati',
  sub: 'Soldi a rischio prima di ogni lavoro',
  concorrenza: { text: 'Sì', color: '#dc2626' },
  volaptic: { text: 'No', color: '#16a34a' }
},
{
  label: 'Contatto sbloccato solo se ti sceglie',
  sub: 'Il cliente legge e decide, tu non paghi per niente',
  concorrenza: { text: 'No', color: '#dc2626' },
  volaptic: { text: 'Sì', color: '#16a34a' }
},
{
  label: 'Solo clienti davvero interessati',
  sub: 'Clienti fantasma, nessuno risponde',
  concorrenza: { text: 'Variabile', color: '#d97706' },
  volaptic: { text: 'Sì', color: '#16a34a' }
},
{
  label: 'Zona di lavoro: la scegli tu',
  sub: 'Richieste fuori zona, tempo sprecato',
  concorrenza: { text: 'Parziale', color: '#d97706' },
  volaptic: { text: 'Totale', color: '#16a34a' }
},
{
  label: 'Nessun profilo falso',
  sub: 'Concorrenza sleale non verificata',
  concorrenza: { text: 'Parziale', color: '#d97706' },
  volaptic: { text: 'Sì', color: '#16a34a' }
},
{
  label: 'Profilo gratuito',
  sub: 'Abbonamenti fissi anche senza lavori',
  concorrenza: { text: 'No', color: '#dc2626' },
  volaptic: { text: 'Sì', color: '#16a34a' }
},
{
  label: 'Supporto reale al professionista',
  sub: 'Telefono assente, nessuno risponde',
  concorrenza: { text: 'Limitato', color: '#d97706' },
  volaptic: { text: 'Sì', color: '#16a34a' }
}];


const summaryRows = [
{ label: 'Paghi per rispondere', concorrenza: '✗ Sì', volaptic: '✓ No' },
{ label: 'Paghi anche senza lavoro', concorrenza: '✗ Sì', volaptic: '✓ No' },
{ label: 'Clienti davvero interessati', concorrenza: '✗ Spesso no', volaptic: '✓ Sì' },
{ label: 'Lavori solo nella tua zona', concorrenza: '✗ Parziale', volaptic: '✓ Tutta Italia' },
{ label: 'Contatto solo se ti scelgono', concorrenza: '✗ No', volaptic: '✓ Sì' }];


const SignupForm = () => {
  const [formData, setFormData] = useState({
    profession: '',
    email: '',
    password: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Dynamic professions state
  const [professions, setProfessions] = useState([]);
  const [professionsLoading, setProfessionsLoading] = useState(true);
  const [professionDropdownOpen, setProfessionDropdownOpen] = useState(false);
  const [professionSearch, setProfessionSearch] = useState('');
  const professionDropdownRef = useRef(null);

  // Fetch professions from Supabase on mount
  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const { data, error } = await supabase?.from('professions')?.select('id, name, category')?.order('category', { ascending: true })?.order('name', { ascending: true });

        if (error) {
          console.error('Error fetching professions:', error);
        } else {
          setProfessions(data || []);
        }
      } catch (err) {
        console.error('Unexpected error fetching professions:', err);
      } finally {
        setProfessionsLoading(false);
      }
    };

    fetchProfessions();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (professionDropdownRef?.current && !professionDropdownRef?.current?.contains(e?.target)) {
        setProfessionDropdownOpen(false);
        setProfessionSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group professions by category
  const groupedProfessions = professions?.reduce((acc, prof) => {
    const cat = prof?.category || 'Altro';
    if (!acc?.[cat]) acc[cat] = [];
    acc?.[cat]?.push(prof);
    return acc;
  }, {});

  // Filtered grouped professions based on search
  const filteredGrouped = Object.entries(groupedProfessions)?.reduce((acc, [cat, profs]) => {
    const filtered = profs?.filter((p) =>
    p?.name?.toLowerCase()?.includes(professionSearch?.toLowerCase())
    );
    if (filtered?.length > 0) acc[cat] = filtered;
    return acc;
  }, {});

  // Get selected profession label for display
  const selectedProfessionLabel = professions?.find((p) => p?.id === formData?.profession)?.name || '';

  const professionOptions = PROFESSIONS?.map((prof) => ({
    value: prof,
    label: prof
  }));

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.profession) {
      newErrors.profession = 'Seleziona una professione';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.email?.trim()) {
      newErrors.email = 'L\'email è obbligatoria';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = 'Inserisci un\'email valida';
    }

    if (!formData?.password) {
      newErrors.password = 'La password è obbligatoria';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Minimo 8 caratteri';
    }

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'Devi accettare i termini e condizioni';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleProfessionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      profession: value
    }));
    setProfessionDropdownOpen(false);
    setProfessionSearch('');

    if (errors?.profession) {
      setErrors((prev) => ({
        ...prev,
        profession: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            role: 'professionista',
            profession: selectedProfessionLabel,
            profession_id: formData?.profession,
            email_confirmed: false,
            registration_step: 1
          },
          emailRedirectTo: `${window.location?.origin}/auth/callback`
        }
      });

      if (authError) {
        console.error('❌ Errore di registrazione:', authError);

        if (authError?.message?.includes('fetch') ||
        authError?.message?.includes('Failed to fetch') ||
        authError?.status === 0) {
          setErrors({
            submit: `⚠️ Impossibile connettersi al progetto Supabase "Volaptic".

📋 Segui questi passaggi:

1️⃣ Vai su https://app.supabase.com
2️⃣ Verifica che il progetto "Volaptic" esista
3️⃣ Vai su Project Settings → API
4️⃣ Copia URL e chiave anon/public
5️⃣ Aggiorna il file .env:
   VITE_SUPABASE_URL=tuo-url
   VITE_SUPABASE_ANON_KEY=tua-chiave
6️⃣ Riavvia il server

💡 Controlla anche la console del browser per dettagli.`
          });
        } else if (authError?.message?.includes('User already registered')) {
          setErrors({ submit: 'Questo indirizzo email è già registrato. Prova ad accedere.' });
        } else if (authError?.message?.includes('Invalid email')) {
          setErrors({ submit: 'Indirizzo email non valido.' });
        } else if (authError?.message?.includes('Password')) {
          setErrors({ submit: 'La password non soddisfa i requisiti di sicurezza.' });
        } else {
          setErrors({ submit: authError?.message || 'Errore durante la registrazione' });
        }

        setIsSubmitting(false);
        return;
      }

      setShowSuccess(true);
      setIsSubmitting(false);

    } catch (error) {
      console.error('❌ Errore imprevisto durante la registrazione:', error);

      if (error?.message?.includes('fetch') ||
      error?.message?.includes('NetworkError') ||
      error?.message?.includes('Failed to fetch') ||
      error instanceof TypeError) {
        setErrors({
          submit: `⚠️ Errore di connessione a Supabase.

📋 Verifica:

1️⃣ Il progetto "Volaptic" è stato creato su supabase.com
2️⃣ Le credenziali nel file .env sono corrette
3️⃣ Il progetto è attivo (non in pausa)
4️⃣ La tua connessione internet funziona

💡 Apri la console del browser (F12) per maggiori dettagli.`
        });
      } else {
        setErrors({ submit: 'Errore imprevisto. Riprova più tardi.' });
      }

      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 pt-20 md:pt-24 pb-12 md:pb-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="card text-center py-12 md:py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-success/10 rounded-full mb-6">
                <Icon name="CheckCircle" size={40} color="var(--color-success)" strokeWidth={2} />
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Registrazione completata
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-lg mx-auto mb-8">
                Controlla la tua email per attivare il profilo e procedere con il passo successivo.
              </p>
              <Link to="/login-form">
                <Button variant="default" size="lg">
                  Torna al Login
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>);

  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/assets/images/signup_form_italian_apartment_bg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      <Header />
      <style>{`
        @media (max-width: 767px) {
          .responsive-hero-img { max-height: 140px !important; }
          .signup-constrained { max-width: 100% !important; padding: 0 1rem !important; }
        }
      `}</style>
      <main className="flex-1 pt-20 md:pt-24">

        {/* SECTION 1 — TOP HERO */}
        <section className="bg-transparent px-4 md:px-6 lg:px-8 pt-10 pb-8">
          <div className="max-w-7xl mx-auto text-center">
            {/* Benefit Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto">
              {/* Card 1 */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }} className="flex flex-col items-center text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2780b5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-4">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <h3 className="font-bold text-foreground text-base mb-1">Richieste nella tua zona</h3>
                <p className="text-sm text-muted-foreground">Ricevi solo lavori vicini a te</p>
              </div>
              {/* Card 2 */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }} className="flex flex-col items-center text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2780b5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-4">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <h3 className="font-bold text-foreground text-base mb-1">Rispondi quando vuoi</h3>
                <p className="text-sm text-muted-foreground">Solo alle richieste che ti interessano</p>
              </div>
              {/* Card 3 */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }} className="flex flex-col items-center text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2780b5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-4">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="font-bold text-foreground text-base mb-1">Nessun abbonamento</h3>
                <p className="text-sm text-muted-foreground">Nessun costo per richieste inutili</p>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Il modo più semplice per trovare nuovi clienti
            </h1>
            <p className="text-base md:text-lg mb-6 max-w-2xl mx-auto text-[rgba(39,2,2,0.702)]">
              Iscriviti come professionista e ricevi richieste di lavoro nella tua zona
            </p>
          </div>
        </section>

        {/* SECTION 2 — FORM (centered) */}
        <section className="bg-transparent px-4 md:px-6 lg:px-8 py-8 md:py-10">
          <div className="signup-constrained" style={{ maxWidth: '560px', margin: '0 auto', width: '100%' }}>
            <div className="card">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Profession Select */}
                <div className="relative" ref={professionDropdownRef}>
                  <label className="text-sm font-medium leading-none mb-2 block text-foreground">
                    Professione <span className="text-destructive ml-1">*</span>
                  </label>
                  <button
                    type="button"
                    className={`flex h-10 w-full items-center justify-between rounded-md border bg-white text-black px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors?.profession ? 'border-destructive focus:ring-destructive' : 'border-input'} ${!formData?.profession ? 'text-muted-foreground' : ''}`}
                    onClick={() => {setProfessionDropdownOpen((o) => !o);setProfessionSearch('');}}
                    disabled={professionsLoading}>

                    <span className="truncate">
                      {professionsLoading ? 'Caricamento...' : selectedProfessionLabel || 'Seleziona la tua professione'}
                    </span>
                    <svg className={`h-4 w-4 transition-transform ${professionDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
                  </button>

                  {professionDropdownOpen &&
                  <div className="absolute z-50 w-full mt-1 bg-white text-black border border-border rounded-md shadow-md">
                      {/* Search */}
                      <div className="p-2 border-b">
                        <div className="relative">
                          <svg className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                          <input
                          type="text"
                          placeholder="Cerca professione..."
                          value={professionSearch}
                          onChange={(e) => setProfessionSearch(e?.target?.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          autoFocus />

                        </div>
                      </div>
                      {/* Grouped options */}
                      <div className="py-1 max-h-60 overflow-auto">
                        {Object.keys(filteredGrouped)?.length === 0 ?
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                            {professionSearch ? 'Nessun risultato' : 'Nessuna professione disponibile'}
                          </div> :

                      Object.entries(filteredGrouped)?.map(([category, profs]) =>
                      <div key={category}>
                              <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                                {category}
                              </div>
                              {profs?.map((prof) =>
                        <div
                          key={prof?.id}
                          className={`relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${formData?.profession === prof?.id ? 'bg-primary text-primary-foreground' : ''}`}
                          onClick={() => handleProfessionChange(prof?.id)}>

                                  {prof?.name}
                                </div>
                        )}
                            </div>
                      )
                      }
                      </div>
                    </div>
                  }

                  <p className="text-sm text-muted-foreground mt-1">Potrai aggiungere altre professioni in seguito</p>
                  {errors?.profession && <p className="text-sm text-destructive mt-1">{errors?.profession}</p>}
                </div>


                {/* Email Input */}
                <Input
                  type="email"
                  label="Email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="tuo@email.it"
                  required
                  description="Useremo questa email per accedere al tuo account e ricevere richieste"
                  error={errors?.email}
                  style={{ fontSize: '16px' }} />


                {/* Password Input */}
                <Input
                  type="password"
                  label="Password"
                  name="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Minimo 8 caratteri"
                  required
                  description="Minimo 8 caratteri"
                  error={errors?.password}
                  style={{ fontSize: '16px' }} />


                {/* Terms Checkbox */}
                <Checkbox
                  id="acceptTerms"
                  name="acceptTerms"
                  checked={formData?.acceptTerms}
                  onChange={handleChange}
                  required
                  label={
                  <span className="text-sm">
                      Accetto i{' '}
                      <Link to="/terms-and-conditions" className="text-primary hover:underline">
                        Termini e Condizioni
                      </Link>
                      {' '}e la{' '}
                      <Link to="/privacy-policy" className="text-primary hover:underline">
                        Privacy Policy
                      </Link>
                    </span>
                  }
                  error={errors?.acceptTerms} />


                {/* Submit Error */}
                {errors?.submit &&
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive whitespace-pre-line">{errors?.submit}</p>
                  </div>
                }

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  disabled={isSubmitting}>

                  {isSubmitting ? 'Creazione account...' : 'Crea account'}
                </Button>

                {/* Login Link */}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Hai già un account?{' '}
                    <Link to="/login-form" className="text-primary font-medium hover:underline">
                      Accedi
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* BANNERS — between form section and comparison tables */}
        <section className="px-4 md:px-6 lg:px-8">
          <div style={{ maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto', width: '100%', display: 'block' }}>

            {/* Banner 1 — Red testimonial */}
            <div style={{
              background: '#fff1f1',
              border: '0.5px solid #fca5a5',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              margin: '2rem 0 0.75rem',
              boxSizing: 'border-box',
              textAlign: 'left'
            }}>
              <p style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                color: '#991b1b',
                fontWeight: 500,
                letterSpacing: '0.05em',
                margin: 0
              }}>COME FUNZIONA LA MAGGIOR PARTE DELLE PIATTAFORME</p>
              <p style={{
                fontSize: '14px',
                color: '#7f1d1d',
                lineHeight: 1.6,
                marginTop: '6px',
                marginBottom: 0
              }}>Paghi un costo fisso per ogni contatto. Anche se il cliente non risponde, 
anche se sparisce, anche se il lavoro non si fa.
Risultato: budget bruciato, lavori veri pochi.</p>
            </div>

            {/* Banner 2 — Dark blue value prop */}
            <div style={{
              background: '#1e3a5f',
              borderRadius: '12px',
              padding: '1.25rem 2rem',
              margin: '0 0 2rem',
              boxSizing: 'border-box',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.6)',
                fontWeight: 500,
                letterSpacing: '0.05em',
                margin: 0
              }}>Con Volaptic</p>
              <p style={{
                fontSize: '15px',
                color: '#ffffff',
                fontWeight: 500,
                lineHeight: 1.6,
                marginTop: '6px',
                marginBottom: 0
              }}>
                Ricevi richieste reali nella tua zona.<br />
                Rispondi solo a quelle che ti interessano.<br />
                Il contatto arriva solo se il cliente ha scelto te.
              </p>
            </div>

          </div>
        </section>

        {/* GRADIENT FADE — background image fades to white */}
        <div style={{ height: '120px', background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, #ffffff 100%)', pointerEvents: 'none' }} />

        {/* SECTION 3 — COMPARISON TABLES */}
        <section className="bg-white px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="signup-constrained" style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>

            {/* Section Headline */}
            <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', textAlign: 'center', marginBottom: '40px' }}>
              Perché i professionisti scelgono Volaptic
            </h2>

            {/* Table 1 — Detailed (8 rows) */}
            <div className="overflow-x-auto mb-12">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '14px 16px', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '14px', fontWeight: 600, color: '#374151', width: '50%' }}>
                      Per il professionista
                    </th>
                    <th style={{ textAlign: 'center', padding: '14px 16px', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '14px', fontWeight: 600, color: '#374151', width: '25%' }}>
                      Concorrenza
                    </th>
                    <th style={{ textAlign: 'center', padding: '14px 16px', backgroundColor: '#1a4a8a', borderBottom: '2px solid #1a4a8a', fontSize: '14px', fontWeight: 600, color: '#ffffff', width: '25%' }}>
                      Volaptic
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detailedRows?.map((row, idx) =>
                  <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827', display: 'block' }}>{row?.label}</span>
                        <span style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '2px' }}>{row?.sub}</span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '14px 16px', verticalAlign: 'middle', backgroundColor: '#fafafa' }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: row?.concorrenza?.color }}>{row?.concorrenza?.text}</span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '14px 16px', verticalAlign: 'middle', backgroundColor: '#e8f4fd' }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: row?.volaptic?.color }}>{row?.volaptic?.text}</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Table 2 — Compact Summary (5 rows) */}
            <div className="overflow-x-auto mb-10">
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '16px' }}>In sintesi</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 16px', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '14px', fontWeight: 600, color: '#374151', width: '50%' }}></th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '14px', fontWeight: 600, color: '#374151', width: '25%' }}>
                      Concorrenza
                    </th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', backgroundColor: '#1a4a8a', borderBottom: '2px solid #1a4a8a', fontSize: '14px', fontWeight: 600, color: '#ffffff', width: '25%' }}>
                      Volaptic
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows?.map((row, idx) =>
                  <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>{row?.label}</td>
                      <td style={{ textAlign: 'center', padding: '12px 16px', backgroundColor: '#fafafa' }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#dc2626' }}>{row?.concorrenza}</span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px 16px', backgroundColor: '#e8f4fd' }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#22c55e' }}>{row?.volaptic}</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Dark Blue Callout Box */}
            <div style={{ backgroundColor: '#1e3a5f', borderRadius: '12px', padding: '32px 40px', textAlign: 'center' }}>
              <p style={{ color: '#ffffff', fontSize: '16px', lineHeight: '1.8', fontWeight: 400, maxWidth: '640px', margin: '0 auto' }}>
                Ricevi richieste reali nella tua zona.<br />
                Rispondi solo a quelle che ti interessano.<br />
                Il contatto arriva solo se il cliente ha scelto te.
              </p>
            </div>

          </div>
        </section>

      </main>
      <Footer />
    </div>);


};

export default SignupForm;